import { gql } from '@apollo/client'; // eslint-disable-line
import PropTypes from 'prop-types';
import { flatten, get } from 'lodash';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { withMetrics } from 'client/data/api/api-metrics';
import { filterFleetTrims } from 'site-modules/shared/utils/core-page/fleet-trims';

export function buildMpgStylesPath({ make, model, year }) {
  return `makes["${make}"].models["${model}"].years["${year}"].styles`;
}

const StyleMpg = PropTypes.shape({
  id: PropTypes.string,
  engineFuelType: PropTypes.string,
  engineType: PropTypes.string,
  name: PropTypes.string,
  trimName: PropTypes.string,
  publicationState: PropTypes.string,
  submodelName: PropTypes.string,
  mpgData: PropTypes.shape({
    combined: PropTypes.number,
    city: PropTypes.number,
    highway: PropTypes.number,
  }),
  mpgeData: PropTypes.shape({
    combined: PropTypes.number,
    city: PropTypes.number,
    highway: PropTypes.number,
  }),
  evData: PropTypes.shape({
    timeToCharge: PropTypes.number,
    range: PropTypes.number,
    fastChargePortType: PropTypes.string,
  }),
});

export const MpgEntities = {
  StyleMpg,
};

export const MpgModel = createModelSegment('mpg', [
  /**
   * @see buildMpgStylesPath
   * @return Array<StyleMpg>
   */
  {
    path: 'makes["{makeSlug}"].models["{modelSlug}"].years["{year}"].styles',
    resolve({ makeSlug, modelSlug, year }, context) {
      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query($makeSlug: String!, $modelSlug: String!, $year: Int!) {
              modelYears(makeSlug: $makeSlug, modelSlug: $modelSlug, year: $year) {
                modelName
                submodels {
                  name
                  styleList(sortBy: { shareBySales: DESC }) {
                    styles {
                      id
                      engine {
                        engineType
                        fuelType
                        electricMotor {
                          fastChargePortType
                        }
                      }
                      name
                      trimName
                      publicationState
                      epaSpecifications {
                        cityMpg
                        combinedMpg
                        highwayMpg
                        cityMpge
                        combinedMpge
                        highwayMpge
                        batteryChargeTime
                        electricityRange
                      }
                    }
                  }
                }
              }
            }
          `,
          {
            makeSlug,
            modelSlug,
            year: parseInt(year, 10),
          }
        )
        .then(response => get(response, 'modelYears[0]', {}))
        .then(async ({ modelName, submodels }) =>
          flatten(
            submodels &&
              (await Promise.all(
                submodels.map(async ({ name: submodelName, styleList: { styles } }) =>
                  (await filterFleetTrims(styles, context)).map(
                    ({ id, engine, name, trimName, epaSpecifications }) => ({
                      id,
                      engineFuelType: get(engine, 'fuelType'),
                      engineType: get(engine, 'engineType'),
                      name,
                      trimName,
                      submodelName: `${modelName} ${submodelName}`,
                      mpgData: {
                        combined: get(epaSpecifications, 'combinedMpg'),
                        city: get(epaSpecifications, 'cityMpg'),
                        highway: get(epaSpecifications, 'highwayMpg'),
                      },
                      mpgeData: {
                        combined: get(epaSpecifications, 'combinedMpge'),
                        city: get(epaSpecifications, 'cityMpge'),
                        highway: get(epaSpecifications, 'highwayMpge'),
                      },
                      evData: {
                        timeToCharge: get(epaSpecifications, 'batteryChargeTime'),
                        range: get(epaSpecifications, 'electricityRange'),
                        fastChargePortType: get(engine, 'electricMotor.fastChargePortType'),
                      },
                    })
                  )
                )
              ))
          )
        );
    },
  },
]);
